import React, { useState, useEffect } from 'react';
import { useAuth } from './auth';
import { getHomeFeed, searchUsers, getUserPhotos, reportPhoto, uploadPhotos } from './photoFunctions';
// UserProfile and UserProfileButton removed - no longer needed on Home Feed
import { Gallery, styles, showToast } from './components';
import SkeletonGallery from './components/SkeletonGallery';
import ErrorState from './components/ErrorState';
import { useNetworkStatus, isNetworkError, getErrorMessage } from './utils/networkDetection';
import { UserSearchModal } from './components/UserSearchModal';
import { BottomNavBar } from './components/BottomNavBar';
import { CaptionModal } from './components/CaptionModal';

// Home Feed Page
export const HomeFeed = ({ onNavigateToProfile }) => {
  const { user, profile, signOut } = useAuth();
  const [feedPhotos, setFeedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const isOnline = useNetworkStatus();
  const POSTS_PER_PAGE = 20;

  // No need to fetch blocked user IDs in frontend - backend handles all filtering
  
  useEffect(() => {
    if (user) {
      loadHomeFeed(true);
    }
  }, [user]);

  // Clear error when network comes back online
  useEffect(() => {
    if (isOnline && error) {
      setError(null);
      // Optionally auto-retry when coming back online
      if (user && feedPhotos.length === 0) {
        loadHomeFeed(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  const loadHomeFeed = async (reset = false) => {
    if (!user?.id) return;
    if (reset) {
      setLoading(true);
      setOffset(0);
      setFeedPhotos([]);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    try {
      setError(null); // Clear any previous errors
      const currentOffset = reset ? 0 : offset;
      // getHomeFeed now handles all blocking logic at the database level
      const feed = await getHomeFeed(user.id, POSTS_PER_PAGE, currentOffset);
      
      if (reset) {
        setFeedPhotos(feed);
        setOffset(POSTS_PER_PAGE);
      } else {
        setFeedPhotos(prev => [...prev, ...feed]);
        setOffset(prev => prev + POSTS_PER_PAGE);
      }

      // Check if there are more posts to load
      if (feed.length < POSTS_PER_PAGE) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      console.error('Error loading home feed:', error);
      setError(error);
      setHasMore(false);
      // Only show toast for non-network errors (network errors show in ErrorState)
      if (!isNetworkError(error)) {
        showToast(getErrorMessage(error));
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadHomeFeed(false);
    }
  };

  const handleUserSelect = (selectedUser) => {
    // Navigate to the selected user's profile
    onNavigateToProfile(selectedUser.username);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    // Store files and show caption modal
    setSelectedFiles(files);
    setShowCaptionModal(true);
    e.target.value = ''; // Reset input
  };

  const handleCaptionConfirm = async (captions) => {
    setShowCaptionModal(false);
    setUploading(true);
    
    try {
      await uploadPhotos(user.id, selectedFiles, captions);
      // Refresh the home feed after upload
      await loadHomeFeed(true);
    } catch (err) {
      console.error('Upload failed:', err);
      const errorMessage = err.message || err.error?.message || 'Unknown error occurred';
      
      // Handle different error types
      if (errorMessage.includes('inappropriate content')) {
        // NSFW content blocked
        alert(errorMessage);
      } else if (errorMessage.includes('Unable to check content safety') || errorMessage.includes('MODERATION_FAILED')) {
        // Moderation service error (not NSFW)
        alert('Unable to verify content safety at this time. Please try again in a few moments.');
      } else {
        // Other upload errors
        alert(`Upload failed: ${errorMessage}\n\nCommon issues:\n- Storage bucket "photos" may not exist\n- Storage policies may not be configured\n- Check browser console for details`);
      }
    } finally {
      setUploading(false);
      setSelectedFiles(null);
    }
  };

  const handleCaptionCancel = () => {
    setShowCaptionModal(false);
    setSelectedFiles(null);
  };

  return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#a2a9ac',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)'
      }}>
        <style>{styles}</style>
      
      {/* Header */}
      <header style={{
        textAlign: 'center',
        padding: 'clamp(40px, 8vw, 60px) clamp(20px, 5vw, 40px) clamp(30px, 5vw, 40px)',
        background: 'linear-gradient(180deg, #a2a9ac 0%, rgba(162,169,172,0) 100%)',
        position: 'relative'
      }}>
        {/* Title */}
        <div className="home-feed-header-content">
          <h1 style={{
            fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
            fontSize: 'clamp(2em, 8vw, 5em)',
            color: '#312310',
            letterSpacing: '0.08em',
            fontWeight: 400,
            marginBottom: '0',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
            padding: '0 20px',
            boxSizing: 'border-box'
          }}>
            Home Feed
          </h1>

          <p style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 'clamp(0.75em, 3vw, 0.85em)',
            letterSpacing: '0.02em',
            color: '#553918',
            fontWeight: 300,
            marginTop: '10px',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
            padding: '0 20px',
            boxSizing: 'border-box',
            lineHeight: '1.5'
          }}>
            Recent posts from people you follow
          </p>
        </div>
        <div style={{
          width: '60px',
          height: '1px',
          background: '#af731f',
          margin: '30px auto 0'
        }}></div>
      </header>

      {/* Search Modal */}
      <UserSearchModal 
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onSelectUser={handleUserSelect}
        currentUserId={user?.id}
      />

      {/* Caption Modal */}
      {showCaptionModal && selectedFiles && (
        <CaptionModal
          files={selectedFiles}
          onConfirm={handleCaptionConfirm}
          onCancel={handleCaptionCancel}
        />
      )}

      {/* Gallery */}
      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: '0 clamp(20px, 5vw, 40px) clamp(40px, 10vw, 80px)',
        paddingBottom: 'calc(clamp(40px, 10vw, 80px) + 56px + env(safe-area-inset-bottom))',
        WebkitOverflowScrolling: 'touch'
      }}>
        {loading ? (
          <SkeletonGallery count={12} />
        ) : error && feedPhotos.length === 0 ? (
          <ErrorState 
            onRetry={() => loadHomeFeed(true)}
            message={getErrorMessage(error)}
          />
        ) : (
          <>
            <Gallery 
              photos={feedPhotos} 
              onDelete={null}
              canDelete={false}
              showUsername={true}
              currentUserId={user?.id}
              onReport={async (photoId, reporterId, reportedUserId, reason) => {
                try {
                  await reportPhoto('photo', reporterId, reportedUserId, reason, photoId);
                } catch (error) {
                  console.error('Error reporting photo:', error);
                  throw error;
                }
              }}
              onUsernameClick={(username, userId) => {
                // If clicking on own username, just navigate to profile without search
                if (userId && user?.id && (userId === user.id || String(userId) === String(user.id))) {
                  onNavigateToProfile();
                } else {
                  // Navigate to profile page with the username
                  onNavigateToProfile(username);
                }
              }}
            />
            {hasMore && (
              <div style={{
                textAlign: 'center',
                padding: '40px 0'
              }}>
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={{
                    padding: 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 24px)',
                    background: '#312310',
                    color: '#e6c57e',
                    border: 'none',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 'clamp(10px, 2.5vw, 12px)',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    cursor: loadingMore ? 'wait' : 'pointer',
                    transition: 'all 0.4s ease',
                    fontWeight: 300,
                    borderRadius: '4px',
                    opacity: loadingMore ? 0.6 : 1,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Navigation Bar */}
      <BottomNavBar 
        activeTab="home"
        onNavigateHome={() => {}} // Already on home
        onSearchClick={() => setShowSearchModal(true)}
        onProfileClick={onNavigateToProfile}
        onUpload={handleUpload}
        isUploading={uploading}
      />

      </div>
  );
};



